import "./base.js";
import "../helpers/visit_type_options.js";
import "../../../utilities/params.js";

const DEFAULT_OPTIONS = {
  availabilitySelector: true,
  patientTypeSelector: true,
  addressSelector: true,
  views: [], // { title: 'Displayed to user', name: 'used for identification' }
};

AppointmentBooking.SlotPickerModal.Views.Header = class Header extends AppointmentBooking.SlotPickerModal.Views.Base {
  static {
    this.prototype.className = "appointment-booking-slot-picker-modal-header";
    this.prototype.template = "appointment_booking/slot_picker_modal/templates/header";

    // BEHAVIOUR
    this.prototype.events = {
      "click [data-appointment-booking-slot-picker-modal-behaviour=show-view]": "handleClickShowView",
    };
  }

  constructor() {
    super(...arguments);
    this.initialize = this.initialize.bind(this);
    this.handleClickShowView = this.handleClickShowView.bind(this);
    this.handleVisitTypesChange = this.handleVisitTypesChange.bind(this);
    this.handleVisitTypesMenuToggle = this.handleVisitTypesMenuToggle.bind(this);
    this.handleAvailableTimesChange = this.handleAvailableTimesChange.bind(this);
    this.handlePatientTypeChange = this.handlePatientTypeChange.bind(this);
    this.handleResourceLocationChange = this.handleResourceLocationChange.bind(this);
    this.handleResourceIdentityChange = this.handleResourceIdentityChange.bind(this);
    this.render = this.render.bind(this);
    this.contextForRender = this.contextForRender.bind(this);
    this.scopeVisitTypes = this.scopeVisitTypes.bind(this);
    this.visitTypeScope = this.visitTypeScope.bind(this);
    this.visitTypeRecord = this.visitTypeRecord.bind(this);
    this.filtersRecord = this.filtersRecord.bind(this);
    this.configurePatientTypeSelect = this.configurePatientTypeSelect.bind(this);
    this.patientTypeOptions = this.patientTypeOptions.bind(this);
    this.configureVisitTypesSelect = this.configureVisitTypesSelect.bind(this);
    this.configureAvailableTimesSelect = this.configureAvailableTimesSelect.bind(this);
    this.configureResourceLocationSelect = this.configureResourceLocationSelect.bind(this);
    this.updateVisitTypeItems = this.updateVisitTypeItems.bind(this);
    this.updateVisitTypeDisplay = this.updateVisitTypeDisplay.bind(this);
    this.selectedVisitTypes = this.selectedVisitTypes.bind(this);
    this.updateFilters = this.updateFilters.bind(this);
  }

  initialize(options = {}) {
    super.initialize(options);
    this.options = _.defaults({}, options, DEFAULT_OPTIONS);

    if (this.options.availabilitySelector) {
      this.visitTypeOptions = new AppointmentBooking.SlotPickerModal.Helpers.VisitTypeOptions({
        onChange: () => this.updateVisitTypeItems(),
      });
      this.scopeVisitTypes();
      this.listenTo(this.model.get("resource"), "change:type change:id", this.handleResourceIdentityChange);
    }

    this.listenTo(this.model, "change:selectedView", this.render);
    return this.listenTo(this.model.get("resource"), "change", this.render);
  }

  // EVENT HANDLERS

  handleClickShowView(event) {
    const { view } = event.currentTarget.dataset;
    this.$el.trigger(AppointmentBooking.SlotPickerModal.Events.selectView, [view]);
  }

  handleVisitTypesChange(e) {
    const [values] = e.detail;
    this.updateFilters({ visit_types: values?.[0] || "" });
    this.updateVisitTypeDisplay();
  }

  handleVisitTypesMenuToggle(e) {
    const [open] = e.detail;
    this.$el.find(".js-visit-types-caret")[0]?.toggleAttribute("active", !!open);

    // Mirror sp-select's open-state border on the activator (its --is-active chain
    // lives in sp-select's shadow styles, out of reach of this hand-built trigger)
    const display = this.$el.find(".js-visit-types-display")[0];
    if (!display) {
      return;
    }

    if (open) {
      display.style.setProperty(
        "--sp-comp-text-field-border-color",
        "var(--sp-comp-select-active-border-color, var(--sp-sys-form-field-focus-border-color, var(--sp-sys-color-primary)))",
      );
    } else {
      display.style.removeProperty("--sp-comp-text-field-border-color");
    }
  }

  handleAvailableTimesChange(e) {
    const [availableTimes] = e.detail;
    this.updateFilters({ available_times: availableTimes || "" });
  }

  handlePatientTypeChange(e) {
    const [patientType] = e.detail;
    return this.updateFilters({ patient_type: patientType || "" });
  }

  handleResourceLocationChange(e) {
    const [department_id] = e.detail;
    return this.updateFilters({ department_id: department_id || "" });
  }

  // A modal whose filters don't name its provider scopes by the fetched resource instead,
  // so the scope waits for it to arrive.
  handleResourceIdentityChange() {
    if (!this.filtersRecord()) {
      this.scopeVisitTypes();
    }
  }

  // RENDERING

  render() {
    super.render();

    this.configurePatientTypeSelect();
    this.configureVisitTypesSelect();
    this.configureAvailableTimesSelect();
    this.configureResourceLocationSelect();

    return this;
  }

  contextForRender() {
    const context = super.contextForRender(...arguments);
    context.options = this.options;
    context.title = this.options.title;
    context.views = this.options.views;
    context.showNav = this.options.views.length > 1;
    context.filters = context.filters.toJSON();
    context.resource = context.resource.toJSON();
    context.resource.specialties = _.map(context.resource.specialties, "name").join(", ");

    return context;
  }

  // HELPER METHODS

  // Re-scope the visit type options, e.g. when the patient type changes which slots qualify
  scopeVisitTypes() {
    this.visitTypeOptions?.setScope(this.visitTypeScope());
  }

  // Returns the modal's filters as the filters endpoint's scope params. The endpoint ignores
  // any other key, so an untranslated filter widens the options to the whole publishing domain.
  visitTypeScope() {
    const patientType = this.model.get("filters").get("patient_type");
    const [resultType, recordId] = this.visitTypeRecord() || [];

    return {
      result_types: resultType,
      record_id_scope: recordId,
      new_patient: patientType ? patientType === "new" : undefined,
    };
  }

  // Returns the `[type, id]` of the provider the modal shows slots for: the one its filters
  // name, else the fetched resource. Returns undefined until the resource has loaded.
  visitTypeRecord() {
    const resource = this.model.get("resource");

    if (this.filtersRecord()) {
      return this.filtersRecord();
    }

    if (resource.get("type") && resource.get("id")) {
      return [resource.get("type"), resource.get("id")];
    }
  }

  // Returns the `[type, id]` of the provider the filters name by Sparkle id. A `physician_id`
  // with a `physician_id_type` is an NPI or Epic id, not a Sparkle id.
  filtersRecord() {
    const filters = this.model.get("filters");

    if (filters.get("resource_type") && filters.get("resource_id")) {
      return [filters.get("resource_type"), filters.get("resource_id")];
    }

    if (filters.get("physician_id") && !filters.get("physician_id_type")) {
      return ["Physician", filters.get("physician_id")];
    }
  }

  configurePatientTypeSelect() {
    const select = this.$el.find(".js-patient-type")[0];
    if (select) {
      select.items = this.patientTypeOptions();
      select.addEventListener("input", this.handlePatientTypeChange);
    }
  }

  // Built lazily: the catalog is only available once the widget config has loaded.
  patientTypeOptions() {
    const patientTypeI18n = this.I18n().filters.patient_type;
    return [
      { value: "", title: patientTypeI18n.prompt },
      { value: "new", title: patientTypeI18n.options.new },
      { value: "returning", title: patientTypeI18n.options.returning },
    ];
  }

  configureVisitTypesSelect() {
    const select = this.$el.find(".js-visit-types")[0];
    if (select) {
      this.updateVisitTypeItems();
      select.addEventListener("update-selected", this.handleVisitTypesChange);
      select.addEventListener("update-open", this.handleVisitTypesMenuToggle);
    }
  }

  configureAvailableTimesSelect() {
    const select = this.$el.find(".js-available-times")[0];
    if (select) {
      const timeI18n = this.I18n().filters.time;
      select.items = [
        { value: "", title: timeI18n.prompt },
        { value: "0-11", title: timeI18n.options.morning },
        { value: "12-23", title: timeI18n.options.afternoon },
      ];
      select.addEventListener("input", this.handleAvailableTimesChange);
    }
  }

  configureResourceLocationSelect() {
    const select = this.$el.find(".js-resource-location")[0];
    if (select) {
      const addresses = this.model.get("resource")?.toJSON()?.addresses || [];
      select.items = _.map(addresses, (address) => ({
        value: address.department_id,
        title: address.address_nickname_with_fallback,
        subtitle: this.addressSubtitle(address),
      }));
      select.addEventListener("input", this.handleResourceLocationChange);
    }
  }

  // Pushes the current visit type options into the accordion select, nesting child
  // types under their category. Runs on render and whenever the fetched or scoped
  // visit types change, so it must not (re-)attach listeners.
  updateVisitTypeItems() {
    const select = this.$el.find(".js-visit-types")[0];
    if (!select) {
      return;
    }

    const items = [{ value: "", title: this.I18n().filters.visit_types.prompt }];
    const itemsByValue = {};
    _.each(this.visitTypeOptions.optionsFor(this.selectedVisitTypes()), (visitType) => {
      const item = { value: visitType.value, title: visitType.text };
      const parent = visitType.parent != null ? itemsByValue[visitType.parent] : undefined;
      if (parent) {
        parent.hasChildren = true;
        (parent.children ??= []).push(item);
      } else {
        items.push(item);
        itemsByValue[visitType.value] = item;
      }
    });

    select.items = items;
    select.selected = [this.selectedVisitTypes()];
    this.updateVisitTypeDisplay();
  }

  // Mirrors the selected option's title into the activator text field (the
  // accordion select has no sp-select shell doing this for us).
  updateVisitTypeDisplay() {
    const display = this.$el.find(".js-visit-types-display")[0];
    if (!display) {
      return;
    }

    const selected = this.selectedVisitTypes();
    const match = selected
      ? _.find(this.visitTypeOptions.optionsFor(selected), (visitType) => String(visitType.value) === String(selected))
      : undefined;
    display.value = match?.text || this.I18n().filters.visit_types.prompt;
  }

  selectedVisitTypes() {
    return this.model.get("filters").get("visit_types") || "";
  }

  // Virtual locations carry no meaningful street address to display.
  addressSubtitle(address) {
    if (address.virtual) {
      return "";
    }

    const cityLineParts = _.compact([address.city, _.compact([address.state, address.zip_code]).join(" ")]);

    return _.compact([address.street_address, cityLineParts.join(", ")]).join(", ");
  }

  updateFilters(filters) {
    // Avoid triggering an unnecessary change event due to adding or overwriting existing blank attributes
    const merged = Sparkle.Utilities.mergeParams(this.model.get("filters").toJSON(), filters);

    return this.model.get("filters").set(merged);
  }
};
